import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import VoyageConfigPanel from './components/VoyageConfigPanel';
import RouteVisualization from './components/RouteVisualization';
import ScenarioSimulator from './components/ScenarioSimulator';
import OptimizationResults from './components/OptimizationResults';

const VoyagePlanning = () => {
  const [voyageConfig, setVoyageConfig] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [optimizedRoutes, setOptimizedRoutes] = useState([]);
  const [activeScenario, setActiveScenario] = useState(null);
  const [optimizationResults, setOptimizationResults] = useState(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [activeTab, setActiveTab] = useState('planning');

  // Handle voyage configuration changes
  const handleConfigChange = (config) => {
    setVoyageConfig(config);
  };

  // Handle route optimization
  const handleOptimize = async (config) => {
    setIsOptimizing(true);
    setOptimizationResults(null);

    // Simulate API call for route optimization
    setTimeout(() => {
      const mockOptimizedRoutes = [
        {
          id: 'optimized-direct',
          name: 'AI Optimized Direct',
          type: 'direct',
          distance: '4,785 nm',
          duration: '13.6 days',
          fuelConsumption: '1,080 MT',
          co2Emissions: '3,400 MT',
          euaCost: '$170,000',
          ciiImpact: 'B+',
          waypoints: [
            { lat: 51.9225, lng: 4.4792, name: config?.departurePort || 'Rotterdam' },
            { lat: 1.2966, lng: 103.7764, name: config?.arrivalPort || 'Singapore' }
          ],
          segments: [
            { from: 'Rotterdam', to: 'Suez Canal', emissions: 'Low', weather: 'Optimal' },
            { from: 'Suez Canal', to: 'Singapore', emissions: 'Medium', weather: 'Good' }
          ]
        },
        {
          id: 'optimized-weather',
          name: 'Weather Optimized',
          type: 'weather',
          distance: '4,850 nm',
          duration: '13.2 days',
          fuelConsumption: '1,120 MT',
          co2Emissions: '3,528 MT',
          euaCost: '$176,400',
          ciiImpact: 'B',
          waypoints: [
            { lat: 51.9225, lng: 4.4792, name: config?.departurePort || 'Rotterdam' },
            { lat: 1.2966, lng: 103.7764, name: config?.arrivalPort || 'Singapore' }
          ],
          segments: [
            { from: 'Rotterdam', to: 'Suez Canal', emissions: 'Low', weather: 'Excellent' },
            { from: 'Suez Canal', to: 'Singapore', emissions: 'Medium', weather: 'Optimal' }
          ]
        }
      ];

      const mockResults = {
        optimizedRoute: {
          id: 'optimized-1',
          name: 'AI Optimized Route',
          confidence: 94,
          improvements: {
            fuelSavings: 165,
            emissionsReduction: 520,
            costSavings: 26000,
            timeReduction: 0.6
          }
        },
        recommendations: [
          {
            type: 'speed',
            title: 'Optimal Speed Profile',
            description: 'Reduce speed to 12.5 knots for first 60% of voyage, then increase to 14 knots',
            impact: 'Saves 145 MT fuel, reduces emissions by 456 MT CO₂',
            priority: 'high',
            icon: 'Gauge'
          },
          {
            type: 'route',
            title: 'Weather Routing Adjustment',
            description: 'Slight northern deviation to avoid adverse weather conditions',
            impact: 'Reduces voyage time by 8 hours, saves 20 MT fuel',
            priority: 'medium',
            icon: 'Cloud'
          },
          {
            type: 'fuel',
            title: 'Alternative Fuel Consideration',
            description: 'Consider LNG for 30% emissions reduction on this route',
            impact: 'Potential 1,176 MT CO₂ reduction, requires fuel availability check',
            priority: 'low',
            icon: 'Fuel'
          }
        ],
        complianceAnalysis: {
          ciiRating: 'B+',
          euaRequired: 3400,
          euaCost: 170000,
          fueleuCompliance: 'Compliant',
          riskLevel: 'Low'
        }
      };

      setOptimizedRoutes(mockOptimizedRoutes);
      setSelectedRoute(mockOptimizedRoutes?.[0]);
      setOptimizationResults(mockResults);
      setIsOptimizing(false);
      setActiveTab('results');
    }, 3000);
  };

  // Handle route selection
  const handleRouteSelect = (route) => {
    setSelectedRoute(route);
  };

  // Handle scenario changes
  const handleScenarioChange = (scenario) => {
    setActiveScenario(scenario);
  };

  // Handle voyage approval
  const handleApproveVoyage = () => {
    console.log('Voyage approved:', {
      config: voyageConfig,
      route: selectedRoute,
      scenario: activeScenario,
      results: optimizationResults
    });
    
    // Show success message or redirect
    alert('Voyage plan approved and sent for execution!');
  };

  // Handle export results
  const handleExportResults = () => {
    console.log('Exporting results...');
    // Simulate file download
    const data = {
      voyageConfig,
      selectedRoute,
      optimizationResults,
      exportedAt: new Date()?.toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `voyage-plan-${Date.now()}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: 'planning', label: 'Route Planning', icon: 'Route' },
    { id: 'scenarios', label: 'Scenarios', icon: 'FlaskConical' },
    { id: 'results', label: 'Results', icon: 'Target', disabled: !optimizationResults }
  ];

  return (
    <div className="min-h-screen bg-background pt-16">
      {/* Page Header */}
      <div className="bg-card border-b border-border">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Route" size={24} className="text-primary" strokeWidth={2} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Voyage Planning</h1>
                <p className="text-muted-foreground">
                  AI-powered route optimization for emissions reduction and cost efficiency
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Link to="/fleet-dashboard">
                <Button variant="outline" iconName="ArrowLeft" iconPosition="left">
                  Back to Fleet
                </Button>
              </Link>
              
              <Link to="/emissions-calculator">
                <Button variant="outline" iconName="Calculator" iconPosition="left">
                  Emissions Calculator
                </Button>
              </Link>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex space-x-1 mt-6">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => !tab?.disabled && setActiveTab(tab?.id)}
                disabled={tab?.disabled}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-maritime ${
                  activeTab === tab?.id
                    ? 'bg-primary text-primary-foreground'
                    : tab?.disabled
                    ? 'text-muted-foreground cursor-not-allowed opacity-50'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={tab?.icon} size={16} strokeWidth={2} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="p-6">
        {activeTab === 'planning' && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
            {/* Configuration Panel */}
            <div className="xl:col-span-1">
              <VoyageConfigPanel
                onConfigChange={handleConfigChange}
                onOptimize={handleOptimize}
                isOptimizing={isOptimizing}
              />
            </div>

            {/* Route Visualization */}
            <div className="xl:col-span-2">
              <RouteVisualization
                selectedRoute={selectedRoute}
                routes={optimizedRoutes}
                onRouteSelect={handleRouteSelect}
                isOptimizing={isOptimizing}
              />
            </div>
          </div>
        )}

        {activeTab === 'scenarios' && (
          <div className="max-w-4xl mx-auto">
            <ScenarioSimulator
              baseRoute={selectedRoute}
              onScenarioChange={handleScenarioChange}
            />
          </div>
        )}

        {activeTab === 'results' && (
          <div className="max-w-6xl mx-auto">
            <OptimizationResults
              results={optimizationResults}
              onApproveVoyage={handleApproveVoyage}
              onExportResults={handleExportResults}
            />
          </div>
        )}
      </div>
      {/* Quick Actions Floating Panel */}
      {voyageConfig && (
        <div className="fixed bottom-6 left-6 bg-card border border-border rounded-lg shadow-maritime-lg p-4 max-w-sm">
          <div className="flex items-center space-x-3 mb-3">
            <Icon name="Info" size={16} className="text-accent" strokeWidth={2} />
            <span className="text-sm font-medium text-foreground">Quick Status</span>
          </div>
          
          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Route:</span>
              <span className="text-foreground font-medium">
                {voyageConfig?.departurePort ? 'Configured' : 'Pending'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Optimization:</span>
              <span className="text-foreground font-medium">
                {isOptimizing ? 'Running...' : optimizationResults ? 'Complete' : 'Pending'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Scenarios:</span>
              <span className="text-foreground font-medium">
                {activeScenario ? '1 Active' : 'None'}
              </span>
            </div>
          </div>

          {optimizationResults && (
            <div className="mt-3 pt-3 border-t border-border">
              <Button
                onClick={handleApproveVoyage}
                size="sm"
                iconName="Check"
                iconPosition="left"
                className="w-full bg-success hover:bg-success/90 text-success-foreground"
              >
                Approve & Execute
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VoyagePlanning;